import { DayPicker } from '@daypicker/react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayIn, dayLocale, daysBetween, showDate, toDate, toIso, type IsoDate } from './dates';
import { CalendarIcon } from './icons';
import { Popover } from './Popover';

export interface DateRange {
  from: IsoDate | null;
  to: IsoDate | null;
}

export interface DateRangeInputProps {
  label: string;
  value: DateRange;
  onChange: (value: DateRange) => void;
  /** What its first and last days are called inside: `From`, `To`. */
  fromLabel?: string;
  toLabel?: string;
  /** It must be filled in: its label is marked with a star. */
  required?: boolean;
}

type End = 'from' | 'to';

/**
 * A first and a last day in one field, either may be left out — a policy from and to, a passport
 * to a day alone. Opened, each end is typed in its own box (`14.10.2026`, `14 okt`, `2026-10-14`)
 * or picked on two months: the end being said is picked, the first then moves on to the last,
 * the last closes it; while the last is asked for, the days to the one pointed at are shown.
 */
export function DateRangeInput({
  label,
  value,
  onChange,
  fromLabel,
  toLabel,
  required = false,
}: DateRangeInputProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const id = useId();
  const [open, setOpen] = useState(false);
  const [end, setEnd] = useState<End>('from');
  const [hovered, setHovered] = useState<IsoDate | null>(null);
  // What is being typed in each box, until it is a day.
  const [typed, setTyped] = useState<Record<End, string | null>>({ from: null, to: null });
  const from = value.from === null ? undefined : toDate(value.from);
  const to = value.to === null ? undefined : toDate(value.to);
  const said = (day: Date | undefined) => (day ? showDate(day, lang) : '');

  let shown = t('range.placeholder');
  if (from && to) {
    const days = t('range.days', { count: daysBetween(from, to) + 1 });
    shown = `${said(from)} → ${said(to)} · ${days}`;
  } else if (from) {
    shown = t('range.since', { day: said(from) });
  } else if (to) {
    shown = t('range.until', { day: said(to) });
  }

  // A day put at one end; the last before the first starts the range again from it.
  const put = (which: End, day: IsoDate | null) => {
    if (which === 'from') {
      onChange({
        from: day,
        to: day !== null && value.to !== null && value.to < day ? null : value.to,
      });
    } else if (day !== null && value.from !== null && day < value.from) {
      onChange({ from: day, to: null });
    } else {
      onChange({ from: value.from, to: day });
    }
  };
  // The text of a box read as a day, near the other end or today; left empty, the end is none.
  const read = (which: End) => {
    const text = typed[which];
    if (text === null) return;
    setTyped((was) => ({ ...was, [which]: null }));
    if (text.trim() === '') {
      put(which, null);
      return;
    }
    // In a date's box `14.10` or `14/10` is a day and a month, never a clock.
    const [whole, day = '', month = ''] = /^\s*(\d{1,2})[./](\d{1,2})\s*$/.exec(text) ?? [];
    const written = whole === undefined ? text : `${day}.${month}.`;
    const found = dayIn(written, value.from ?? value.to ?? toIso(new Date()));
    if (found !== null) put(which, found);
  };
  // While the last day is asked for, the days from the first to the one pointed at.
  const preview =
    end === 'to' && value.from !== null && hovered !== null && hovered > value.from
      ? { from, to: toDate(hovered) }
      : undefined;

  const box = (which: End, words: string, day: Date | undefined) => (
    <label className={`range-end${end === which ? ' range-end-active' : ''}`}>
      <span>{words}</span>
      <input
        className="text-input"
        type="text"
        autoComplete="off"
        placeholder={t('range.typeHint')}
        value={typed[which] ?? said(day)}
        onFocus={() => {
          setEnd(which);
        }}
        onChange={(event) => {
          setTyped((was) => ({ ...was, [which]: event.target.value }));
        }}
        onBlur={() => {
          read(which);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            read(which);
          }
        }}
      />
    </label>
  );

  return (
    <div className="field">
      <span className="field-label" id={`${id}-label`} data-required={required || undefined}>
        {label}
      </span>
      <Popover
        open={open}
        onOpenChange={(opening) => {
          setOpen(opening);
          // Opened, the end said first is the first unless only it is there.
          if (opening) setEnd(value.from !== null && value.to === null ? 'to' : 'from');
        }}
        label={label}
        trigger={(props) => (
          <button
            type="button"
            className="pick-trigger"
            aria-labelledby={`${id}-label ${id}-value`}
            {...props}
          >
            <span id={`${id}-value`} className={from || to ? undefined : 'pick-placeholder'}>
              {shown}
            </span>
            <CalendarIcon />
          </button>
        )}
      >
        <div className="range-input">
          <div className="range-ends">
            {box('from', fromLabel ?? t('range.from'), from)}
            <span aria-hidden="true">→</span>
            {box('to', toLabel ?? t('range.to'), to)}
          </div>
          <DayPicker
            mode="range"
            selected={{ from, to }}
            defaultMonth={(end === 'to' ? (to ?? from) : (from ?? to)) ?? new Date()}
            numberOfMonths={2}
            weekStartsOn={1}
            locale={dayLocale(lang)}
            modifiers={preview ? { preview } : {}}
            modifiersClassNames={{ preview: 'rdp-preview' }}
            onDayMouseEnter={(day) => {
              setHovered(toIso(day));
            }}
            onDayMouseLeave={() => {
              setHovered(null);
            }}
            onSelect={(_range, day) => {
              const picked = toIso(day);
              put(end, picked);
              // The first picked, on to the last; the last, done.
              if (end === 'from') setEnd('to');
              else if (value.from === null || picked >= value.from) setOpen(false);
            }}
          />
          {(from || to) && (
            <div className="range-foot">
              <span>{shown}</span>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  onChange({ from: null, to: null });
                  setEnd('from');
                }}
              >
                {t('range.clear')}
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}
